import { supabase } from "@/integrations/supabase/client";

/**
 * Generate a unique event ID for deduplication between client and server events
 */
export function generateEventId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Hash string with SHA-256 for PII (Personal Identifiable Information)
 */
async function hashSHA256(value: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(value.toLowerCase().trim());
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Identify user to TikTok Pixel with automatic SHA-256 hashing
 */
export async function identifyTikTokUser(userData: {
  email?: string;
  phone_number?: string;
  external_id?: string;
}) {
  if (typeof window === 'undefined' || !window.ttq) return;
  
  const hashedData: any = {};
  
  if (userData.email) {
    hashedData.email = await hashSHA256(userData.email);
  }
  if (userData.phone_number) {
    hashedData.phone_number = await hashSHA256(userData.phone_number);
  }
  if (userData.external_id) {
    hashedData.external_id = await hashSHA256(userData.external_id);
  }
  
  window.ttq.identify(hashedData);
  console.log('TikTok user identified (hashed)');
}

/**
 * Track event to both TikTok Pixel (client-side) and server-side API
 */
export async function trackTikTokEvent(
  event: string,
  params?: {
    value?: number;
    currency?: string;
    content_type?: string;
    content_id?: string;
    content_name?: string;
    contents?: Array<{
      content_id: string;
      content_type?: string;
      content_name?: string;
      quantity?: number;
      price?: number;
      brand?: string;
    }>;
  },
  options?: {
    event_id?: string;
    user?: {
      email?: string;
      external_id?: string;
      phone_number?: string;
    };
  }
) {
  const eventId = options?.event_id || generateEventId(event.toLowerCase());

  // Track client-side with TikTok Pixel
  if (typeof window !== 'undefined' && window.ttq) {
    try {
      window.ttq.track(event, params, { event_id: eventId });
      console.log('TikTok Pixel tracked:', event, eventId);
    } catch (error) {
      console.error('TikTok Pixel tracking error:', error);
    }
  }

  // Track server-side via edge function (deferred to avoid blocking render)
  setTimeout(async () => {
    try {
      const { error } = await supabase.functions.invoke('tiktok-track-event', {
        body: {
          event,
          event_id: eventId,
          user: options?.user,
          properties: params,
        },
      });

      if (error) {
        console.error('TikTok server-side tracking error:', error);
      } else {
        console.log('TikTok server-side tracked:', event, eventId);
      }
    } catch (error) {
      console.error('TikTok server-side tracking exception:', error);
    }
  }, 0);

  return eventId;
}

/**
 * Track purchase/payment completion
 */
export async function trackTikTokPurchase(
  orderId: string,
  amount: number,
  productType: string,
  userEmail?: string,
  userId?: string
) {
  return trackTikTokEvent(
    'Purchase',
    {
      contents: [{
        content_id: 'kit-marca-01',
        content_type: 'product',
        content_name: 'Kit Marca Completo',
        price: 100,
        quantity: 1,
      }],
      value: 100,
      currency: 'BRL',
    },
    {
      event_id: `order_${orderId}`,
      user: {
        email: userEmail,
        external_id: userId,
      },
    }
  );
}

/**
 * Track checkout initiation
 */
export async function trackTikTokInitiateCheckout(
  productType: string,
  amount: number,
  userEmail?: string,
  userId?: string
) {
  return trackTikTokEvent(
    'InitiateCheckout',
    {
      contents: [{
        content_id: productType,
        content_type: 'product',
        content_name: productType === 'brand_kit' ? 'Kit Marca Completo' : 'Mascote Essencial',
      }],
      value: 100,
      currency: 'BRL',
    },
    {
      user: {
        email: userEmail,
        external_id: userId,
      },
    }
  );
}

/**
 * Track add to cart
 */
export async function trackTikTokAddToCart(
  productType: string,
  amount: number,
  userEmail?: string,
  userId?: string
) {
  return trackTikTokEvent(
    'AddToCart',
    {
      contents: [{
        content_id: productType,
        content_type: 'product',
        content_name: productType === 'brand_kit' ? 'Kit Marca Completo' : 'Mascote Essencial',
      }],
      value: 100,
      currency: 'BRL',
    },
    {
      user: {
        email: userEmail,
        external_id: userId,
      },
    }
  );
}

/**
 * Track lead generation (e.g., logo created, brand kit started)
 */
export async function trackTikTokLead(
  contentType: string,
  userEmail?: string,
  userId?: string
) {
  return trackTikTokEvent(
    'SubmitForm',
    {
      contents: [{
        content_id: contentType,
        content_type: 'product',
        content_name: contentType,
      }],
      value: 100,
      currency: 'BRL',
    },
    {
      user: {
        email: userEmail,
        external_id: userId,
      },
    }
  );
}

/**
 * Track registration completion
 */
export async function trackTikTokCompleteRegistration(
  userEmail?: string,
  userId?: string
) {
  return trackTikTokEvent(
    'CompleteRegistration',
    {
      contents: [{
        content_id: 'kit-marca-registro',
        content_type: 'product',
        content_name: 'Registro Kit Marca',
      }],
      value: 100,
      currency: 'BRL',
    },
    {
      user: {
        email: userEmail,
        external_id: userId,
      },
    }
  );
}

/**
 * Track ViewContent - for landing pages and product pages
 */
export async function trackTikTokViewContent(params: {
  content_id: string;
  content_name: string;
  content_type?: string;
  value?: number;
  currency?: string;
  brand?: string;
}) {
  const price = params.value || 100;
  
  return trackTikTokEvent(
    'ViewContent',
    {
      contents: [{
        content_id: params.content_id,
        content_type: params.content_type || 'product',
        content_name: params.content_name,
        price: price,
        brand: params.brand || 'ClickLogos',
      }],
      value: price,
      currency: params.currency || 'BRL',
    }
  );
}

/**
 * Track AddPaymentInfo - when user adds payment details
 */
export async function trackTikTokAddPaymentInfo(params: {
  content_id: string;
  content_name: string;
  value: number;
  currency?: string;
  userEmail?: string;
  userId?: string;
}) {
  return trackTikTokEvent(
    'AddPaymentInfo',
    {
      content_id: params.content_id,
      content_name: params.content_name,
      value: params.value,
      currency: params.currency || 'BRL',
      content_type: 'product',
      contents: [{
        content_id: params.content_id,
        content_name: params.content_name,
        quantity: 1,
        price: params.value,
      }],
    },
    {
      user: {
        email: params.userEmail,
        external_id: params.userId,
      },
    }
  );
}

/**
 * Track PlaceAnOrder - when order is created (before payment)
 */
export async function trackTikTokPlaceAnOrder(params: {
  content_id: string;
  content_name: string;
  value: number;
  currency?: string;
  userEmail?: string;
  userId?: string;
}) {
  return trackTikTokEvent(
    'PlaceAnOrder',
    {
      content_id: params.content_id,
      content_name: params.content_name,
      value: params.value,
      currency: params.currency || 'BRL',
      content_type: 'product',
      contents: [{
        content_id: params.content_id,
        content_name: params.content_name,
        quantity: 1,
        price: params.value,
      }],
    },
    {
      user: {
        email: params.userEmail,
        external_id: params.userId,
      },
    }
  );
}
